// The colour key, and the wind-arrow scale that is no longer beside it.
//
// The key used to be a floating panel over the map's bottom-right. It is four
// words in the ruler's key row now — because for a RATING that is all a key
// needs to be: Poor / Fair / Good / Ideal, the four bands the map is painted in.
//
// A CONTINUOUS variable still needs a ramp: "23" means nothing without knowing
// which end of the scale it is on. So the same slot carries the clipped gradient
// in that case — clipped, because temperature never spans its full -40..48 in
// one frame and showing the whole domain would spend most of the bar on colours
// nothing is painted with. That clip is why this listens for `cityFC` rather
// than being called from the fetching layer: the observed range is a property of
// the cities that loaded.
//
// The arrow key moved into the Layers menu. Arrow length is a property of the
// wind layer, so it belongs beside the switch that turns the layer on, not in a
// key about the field underneath it.

import { $ } from '../dom.ts';
import { state } from '../state.ts';
import { VAR } from '../../lib/variables.ts';
import { observedRange } from '../data.ts';
import { windActive } from '../wind-state.ts';
import { arrowSwatch, ARROW_BINS, CALM_MAX } from '../map/wind-sprites.ts';
import { PALETTES, bandScale, clippedGradientCss, domain } from '../palettes.ts';

export function updateLegend() {
  const p = PALETTES[state.variable];
  const eyebrow = $('ruler-key-eyebrow');
  const scale = $('ruler-key-scale');
  // Swatch colours come from bandScale, which samples the SAME ramp the map
  // paints with — the key cannot drift from the field by construction.
  const bands = bandScale(state.variable);

  if (bands.length) {
    // A rating: four words for the four bands the map is painted in. It used to
    // read "Tick height · rating", back when the ruler's sections encoded the
    // score — they are uniform now, and a caption for something that is not
    // there is worse than none.
    // A rating made from a day is named by the day, here as in the sentence.
    eyebrow.textContent = VAR[state.variable]?.custom && state.daySeed ? `Days like ${state.daySeed.name}` : 'Rating';
    scale.replaceChildren(
      ...bands.map(({ label, color }) => {
        const el = document.createElement('span');
        el.className = 'rk-band';
        el.textContent = label;
        el.style.setProperty('--swatch', color);
        return el;
      }),
    );
    return;
  }

  // A measurement: the ramp names itself ("Temperature · °C"), so an eyebrow
  // above it would only repeat the next line.
  eyebrow.textContent = '';
  const [dlo, dhi] = domain(state.variable);
  const obs = observedRange(state.variable);
  const min = obs ? Math.max(dlo, Math.floor(obs[0])) : dlo;
  const max = obs ? Math.min(dhi, Math.ceil(obs[1])) : dhi;

  const wrap = document.createElement('div');
  wrap.className = 'rk-ramp';
  const label = document.createElement('span');
  label.className = 'rk-ramp-label';
  label.textContent = p.unit ? `${p.label} · ${p.unit}` : p.label;
  const lo = document.createElement('span');
  lo.className = 'rk-ramp-end';
  lo.textContent = String(min);
  const bar = document.createElement('span');
  bar.className = 'rk-ramp-bar';
  bar.style.background = clippedGradientCss(state.variable, min, max);
  const hi = document.createElement('span');
  hi.className = 'rk-ramp-end';
  hi.textContent = String(max);
  wrap.append(label, lo, bar, hi);
  scale.replaceChildren(wrap);
}

export function updateArrowLegend() {
  const box = $('legend-arrows');
  if (!windActive()) {
    box.hidden = true;
    box.replaceChildren();
    return;
  }
  box.hidden = false;
  box.replaceChildren();
  const head = document.createElement('div');
  head.className = 'legend-arrows-title';
  head.innerHTML = `<span>${PALETTES.ws.label}</span> <span class="unit">${PALETTES.ws.unit}</span>`;
  box.append(head);
  const row = document.createElement('div');
  row.className = 'legend-arrows-row';
  let lo = CALM_MAX;
  for (const b of ARROW_BINS) {
    const { url, w, h } = arrowSwatch(b.shaft);
    const cell = document.createElement('div');
    cell.className = 'legend-arrow';
    const img = document.createElement('img');
    img.src = url;
    img.width = w;
    img.height = h;
    img.alt = '';
    const cap = document.createElement('span');
    cap.textContent = b.max === Infinity ? `${lo}+` : `${lo}`;
    cell.append(img, cap);
    row.append(cell);
    lo = b.max;
  }
  box.append(row);
  box.title = 'Arrow length is wind speed; direction is where the wind blows from. A ring means calm.';
}
